package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraImportMapping
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraImportOrder
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraImportPreview
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraImportResult
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraOrder
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraReason
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraSummary
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraUnit
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraUnitSuggestion
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.PurchasesExtraOrderRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class RemotePurchasesExtraOrderRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : PurchasesExtraOrderRepository {
    override suspend fun load(period: MonthPeriod): List<PurchasesExtraOrder> {
        val response =
            httpClient.get("$baseUrl/purchases/extra-orders?year=${period.year}&month=${period.month}") {
                authorize()
            }
        ensureSuccess(response)
        return response.body<List<PurchasesExtraOrderDto>>().map { it.toDomain() }
    }

    override suspend fun count(period: MonthPeriod): Long {
        val response =
            httpClient.get("$baseUrl/purchases/extra-orders/count?year=${period.year}&month=${period.month}") {
                authorize()
            }
        ensureSuccess(response)
        return response.bodyAsText().trim().toLongOrNull() ?: 0L
    }

    override suspend fun summary(period: MonthPeriod): PurchasesExtraSummary {
        val response =
            httpClient.get("$baseUrl/purchases/extra-orders/summary?year=${period.year}&month=${period.month}") {
                authorize()
            }
        ensureSuccess(response)
        return response.body<PurchasesExtraSummaryDto>().toDomain()
    }

    override suspend fun units(): List<PurchasesExtraUnit> {
        val response = httpClient.get("$baseUrl/purchases/extra-orders/units") { authorize() }
        ensureSuccess(response)
        return response.body<List<PurchasesExtraUnitDto>>().map { it.toDomain() }
    }

    override suspend fun reasons(includeInactive: Boolean): List<PurchasesExtraReason> {
        val response =
            httpClient.get("$baseUrl/purchases/extra-orders/reasons?includeInactive=$includeInactive") {
                authorize()
            }
        ensureSuccess(response)
        return response.body<List<PurchasesExtraReasonDto>>().map { it.toDomain() }
    }

    override suspend fun createReason(name: String): PurchasesExtraReason {
        val response =
            httpClient.post("$baseUrl/purchases/extra-orders/reasons") {
                authorize()
                contentType(ContentType.Application.Json)
                setBody(PurchasesExtraReasonRequestDto(name))
            }
        ensureSuccess(response)
        return response.body<PurchasesExtraReasonDto>().toDomain()
    }

    override suspend fun renameReason(
        id: String,
        name: String,
    ): PurchasesExtraReason {
        val response =
            httpClient.put("$baseUrl/purchases/extra-orders/reasons/$id") {
                authorize()
                contentType(ContentType.Application.Json)
                setBody(PurchasesExtraReasonRequestDto(name))
            }
        ensureSuccess(response)
        return response.body<PurchasesExtraReasonDto>().toDomain()
    }

    override suspend fun changeReasonStatus(
        id: String,
        active: Boolean,
    ): PurchasesExtraReason {
        val response =
            httpClient.patch("$baseUrl/purchases/extra-orders/reasons/$id/status") {
                authorize()
                contentType(ContentType.Application.Json)
                setBody(PurchasesExtraReasonStatusDto(active))
            }
        ensureSuccess(response)
        return response.body<PurchasesExtraReasonDto>().toDomain()
    }

    override suspend fun previewUnits(labels: List<String>): List<PurchasesExtraUnitSuggestion> {
        val response =
            httpClient.post("$baseUrl/purchases/extra-orders/units/preview") {
                authorize()
                contentType(ContentType.Application.Json)
                setBody(PurchasesExtraUnitPreviewRequestDto(labels))
            }
        ensureSuccess(response)
        return response.body<PurchasesExtraUnitPreviewDto>().labels.map { it.toDomain() }
    }

    override suspend fun previewImport(
        period: MonthPeriod,
        orders: List<PurchasesExtraImportOrder>,
    ): PurchasesExtraImportPreview {
        val response =
            httpClient.post("$baseUrl/purchases/extra-orders/import/preview") {
                authorize()
                contentType(ContentType.Application.Json)
                setBody(PurchasesExtraImportRequestDto.from(period, orders, emptyList()))
            }
        ensureSuccess(response)
        return response.body<PurchasesExtraImportPreviewDto>().toDomain()
    }

    override suspend fun importOrders(
        period: MonthPeriod,
        orders: List<PurchasesExtraImportOrder>,
        mappings: List<PurchasesExtraImportMapping>,
    ): PurchasesExtraImportResult {
        val response =
            httpClient.post("$baseUrl/purchases/extra-orders/import") {
                authorize()
                contentType(ContentType.Application.Json)
                setBody(PurchasesExtraImportRequestDto.from(period, orders, mappings))
            }
        ensureSuccess(response)
        return response.body<PurchasesExtraImportResultDto>().toDomain()
    }

    override suspend fun create(
        period: MonthPeriod,
        order: PurchasesExtraOrder,
    ): PurchasesExtraOrder {
        val response =
            httpClient.post("$baseUrl/purchases/extra-orders?year=${period.year}&month=${period.month}") {
                authorize()
                contentType(ContentType.Application.Json)
                setBody(
                    PurchasesExtraOrderUpdateDto(
                        orderDate = order.orderDate,
                        deliveryDate = order.deliveryDate,
                        quantity = order.quantity,
                        description = order.description,
                        reasonId = order.reasonId,
                        unitId = order.unitId,
                    ),
                )
            }
        ensureSuccess(response)
        return response.body<PurchasesExtraOrderDto>().toDomain()
    }

    override suspend fun update(order: PurchasesExtraOrder): PurchasesExtraOrder {
        val response =
            httpClient.put("$baseUrl/purchases/extra-orders/${order.id}") {
                authorize()
                contentType(ContentType.Application.Json)
                setBody(
                    PurchasesExtraOrderUpdateDto(
                        orderDate = order.orderDate,
                        deliveryDate = order.deliveryDate,
                        quantity = order.quantity,
                        description = order.description,
                        reasonId = order.reasonId,
                        unitId = order.unitId,
                    ),
                )
            }
        ensureSuccess(response)
        return response.body<PurchasesExtraOrderDto>().toDomain()
    }

    override suspend fun delete(id: String) {
        val response = httpClient.delete("$baseUrl/purchases/extra-orders/$id") { authorize() }
        ensureSuccess(response)
    }

    private suspend fun ensureSuccess(response: HttpResponse) {
        if (!response.status.isSuccess()) {
            val message =
                runCatching {
                    Json { ignoreUnknownKeys = true }
                        .decodeFromString<PurchasesExtraApiErrorDto>(response.bodyAsText())
                        .message
                }.getOrNull()
            error(message ?: "HTTP ${response.status.value}")
        }
    }

    private fun HttpRequestBuilder.authorize() {
        authRepository.session.value?.token?.let { token ->
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }
}

@Serializable
private data class PurchasesExtraOrderDto(
    val id: String,
    val referenceYear: Int,
    val referenceMonth: Int,
    val orderDate: String,
    val deliveryDate: String? = null,
    val quantity: Int? = null,
    val description: String = "",
    val sourceUnitLabel: String? = null,
    val reasonId: String? = null,
    val reasonName: String? = null,
    val reasonActive: Boolean = false,
    val unitId: String? = null,
    val organogramaUnidadeId: String? = null,
    val unitName: String? = null,
    val sortOrder: Int = 0,
) {
    fun toDomain() =
        PurchasesExtraOrder(
            id = id,
            orderDate = orderDate,
            deliveryDate = deliveryDate,
            quantity = quantity,
            description = description,
            sourceUnitLabel = sourceUnitLabel,
            unitId = unitId,
            unitName = unitName,
            sortOrder = sortOrder,
            organogramaUnidadeId = organogramaUnidadeId,
            reasonId = reasonId,
            reasonName = reasonName,
            reasonActive = reasonActive,
        )
}

@Serializable
private data class PurchasesExtraSummaryDto(
    val orderCount: Long = 0,
    val totalItems: Long = 0,
) {
    fun toDomain() = PurchasesExtraSummary(orderCount = orderCount, totalItems = totalItems)
}

@Serializable
private data class PurchasesExtraUnitDto(
    val id: String,
    val name: String,
    val director: String? = null,
    val manager: String? = null,
    val organogramaUnidadeId: String? = null,
) {
    fun toDomain() =
        PurchasesExtraUnit(
            id = id,
            name = name,
            director = director,
            manager = manager,
            organogramaUnidadeId = organogramaUnidadeId,
        )
}

@Serializable
private data class PurchasesExtraReasonDto(
    val id: String,
    val name: String,
    val active: Boolean = true,
) {
    fun toDomain() = PurchasesExtraReason(id = id, name = name, active = active)
}

@Serializable
private data class PurchasesExtraReasonRequestDto(
    val name: String,
)

@Serializable
private data class PurchasesExtraReasonStatusDto(
    val active: Boolean,
)

@Serializable
private data class PurchasesExtraUnitPreviewRequestDto(
    val labels: List<String>,
)

@Serializable
private data class PurchasesExtraUnitPreviewDto(
    val units: List<PurchasesExtraUnitDto> = emptyList(),
    val labels: List<PurchasesExtraUnitLabelDto> = emptyList(),
)

@Serializable
private data class PurchasesExtraUnitLabelDto(
    val label: String,
    val unitId: String? = null,
    val status: String = "NOT_FOUND",
) {
    fun toDomain() = PurchasesExtraUnitSuggestion(label = label, unitId = unitId, status = status)
}

@Serializable
private data class PurchasesExtraImportRequestDto(
    val year: Int,
    val month: Int,
    val orders: List<PurchasesExtraImportOrderDto>,
    val mappings: List<PurchasesExtraImportMappingDto>,
) {
    companion object {
        fun from(
            period: MonthPeriod,
            orders: List<PurchasesExtraImportOrder>,
            mappings: List<PurchasesExtraImportMapping>,
        ) = PurchasesExtraImportRequestDto(
            year = period.year,
            month = period.month,
            orders = orders.map { it.toDto() },
            mappings = mappings.map { PurchasesExtraImportMappingDto(it.label, it.unitId) },
        )
    }
}

@Serializable
private data class PurchasesExtraImportOrderDto(
    val orderDate: String,
    val deliveryDate: String? = null,
    val quantity: Int? = null,
    val description: String = "",
    val sourceUnitLabel: String? = null,
)

@Serializable
private data class PurchasesExtraImportMappingDto(
    val label: String,
    val unitId: String? = null,
)

@Serializable
private data class PurchasesExtraImportPreviewDto(
    val year: Int,
    val month: Int,
    val orderCount: Int,
    val existingCount: Int,
    val unitCount: Int,
    val willReplace: Boolean,
) {
    fun toDomain() =
        PurchasesExtraImportPreview(
            year = year,
            month = month,
            orderCount = orderCount,
            existingCount = existingCount,
            unitCount = unitCount,
            willReplace = willReplace,
        )
}

@Serializable
private data class PurchasesExtraImportResultDto(
    val year: Int,
    val month: Int,
    val ordersInserted: Int,
    val existingReplaced: Int,
    val mappingsSaved: Int,
    val unitCount: Int,
) {
    fun toDomain() =
        PurchasesExtraImportResult(
            year = year,
            month = month,
            ordersInserted = ordersInserted,
            existingReplaced = existingReplaced,
            mappingsSaved = mappingsSaved,
            unitCount = unitCount,
        )
}

@Serializable
private data class PurchasesExtraOrderUpdateDto(
    val orderDate: String,
    val deliveryDate: String? = null,
    val quantity: Int? = null,
    val description: String = "",
    val reasonId: String? = null,
    val unitId: String? = null,
)

@Serializable
private data class PurchasesExtraApiErrorDto(
    val message: String? = null,
)

private fun PurchasesExtraImportOrder.toDto() =
    PurchasesExtraImportOrderDto(
        orderDate = orderDate,
        deliveryDate = deliveryDate,
        quantity = quantity,
        description = description,
        sourceUnitLabel = sourceUnitLabel,
    )
